// Operator › Settings (`/operator/settings?section=&company=&satellite=`; OPERATOR-CONSOLE): agency
// users only; anyone else gets the not-found page and no API call, as the API answers them 404.
// An agency user who is not a platform admin sees the API's 403 as a notice.
import { createRoute, useNavigate } from "@tanstack/react-router";
import { isOperator } from "../features/company";
import {
  OperatorSettingsPage,
  SETTINGS_SECTIONS,
  type SettingsSection,
} from "../features/operator/settings/settings-page";
import { useSession } from "../features/use-feature";
import { NotFoundPage } from "../pages/status-pages";
import { OperatorShell, PlainShell } from "../shell/app-shell";
import { authedRoute } from "./_authed";

const UUID = /^[0-9a-f-]{36}$/i;

export const operatorSettingsRoute = createRoute({
  getParentRoute: () => authedRoute,
  path: "operator/settings",
  validateSearch: (
    search: Record<string, unknown>,
  ): { section?: SettingsSection; company?: string; satellite?: string } => ({
    ...(SETTINGS_SECTIONS.includes(search.section as SettingsSection)
      ? { section: search.section as SettingsSection }
      : {}),
    ...(typeof search.company === "string" && UUID.test(search.company)
      ? { company: search.company }
      : {}),
    ...(typeof search.satellite === "string" && UUID.test(search.satellite)
      ? { satellite: search.satellite }
      : {}),
  }),
  component: function OperatorSettingsScreen() {
    const { api } = operatorSettingsRoute.useRouteContext();
    const { section, company, satellite } = operatorSettingsRoute.useSearch();
    const navigate = useNavigate();
    const { me } = useSession();
    if (!isOperator(me))
      return (
        <PlainShell me={me} api={api} current={null}>
          <NotFoundPage />
        </PlainShell>
      );
    return (
      <OperatorShell me={me} api={api}>
        <OperatorSettingsPage
          api={api}
          me={me}
          section={section ?? "prices"}
          company={company ?? null}
          satellite={satellite ?? null}
          onChange={(next) =>
            void navigate({
              to: "/operator/settings",
              search: {
                ...(next.section !== "prices" ? { section: next.section } : {}),
                ...(next.company ? { company: next.company } : {}),
                ...(next.satellite ? { satellite: next.satellite } : {}),
              },
            })
          }
        />
      </OperatorShell>
    );
  },
});
